import { useState } from "react"
import { Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { fieldInputClass, fieldLabelClass, focusRing, formButtonClass, primaryButtonClass } from "../lightTheme"
import { auditCompanies, searchCopy } from "./auditDatabase"
import { findAuditMatches } from "./findAuditMatches"

const cardShadow =
  "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_2px_2px_0_rgba(0,0,0,0.04),0_8px_16px_-4px_rgba(0,0,0,0.04),0_0_0_1px_#FAFAFA]"

const headingClass =
  "text-balance text-[40px] font-bold leading-[48px] tracking-[-0.04em] text-[#171717] max-md:text-[28px] max-md:leading-8"

const longButtonClass = cn(
  primaryButtonClass,
  "h-auto min-h-10 max-w-full whitespace-normal px-4 py-2 text-left"
)

export function AuditCard({ company }) {
  const facts = [
    `Measured First Response Time: ${company.response}`,
    `Industry Benchmark Rank: ${company.rank}`,
    `Estimated Monthly Revenue Leakage: ${company.leakage}`,
  ]

  return (
    <article className={`flex w-full flex-col gap-6 rounded-xl bg-white p-6 ${cardShadow}`}>
      <div className="flex flex-col gap-2">
        <h3 className="text-[24px] font-bold leading-8 tracking-[-0.04em] text-[#171717]">
          {`${company.name} — ${company.location}`}
        </h3>
        <p className="text-[14px] font-normal leading-5 text-[#4D4D4D]">
          {`Audit Status: ${company.auditStatus}`}
        </p>
      </div>
      <ul className="m-0 flex list-disc flex-col gap-3 pl-5 text-[16px] font-normal leading-6 text-[#4D4D4D]">
        {facts.map((fact) => (
          <li key={fact}>{fact}</li>
        ))}
      </ul>
      {company.diagnosis ? (
        <p className="text-[16px] font-normal leading-6 text-[#4D4D4D]">
          <span className="font-semibold text-[#171717]">Diagnosis: </span>
          {company.diagnosis}
        </p>
      ) : null}
      <button type="button" className={longButtonClass}>
        {searchCopy.foundCta}
      </button>
    </article>
  )
}

function MissingCard({ domain }) {
  return (
    <form
      className={`flex w-full flex-col gap-6 rounded-xl bg-white p-6 ${cardShadow}`}
      onSubmit={(event) => event.preventDefault()}
    >
      <div className="flex flex-col gap-3">
        <h3 className="text-balance text-[24px] font-bold leading-8 tracking-[-0.04em] text-[#171717]">
          {searchCopy.missingTitle}
        </h3>
        <p className="text-[16px] font-normal leading-6 text-[#4D4D4D]">
          {searchCopy.missingBody}
        </p>
      </div>
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-2" htmlFor="benchmark-domain">
          <span className={fieldLabelClass}>{searchCopy.domainLabel}</span>
          <input
            id="benchmark-domain"
            type="text"
            name="domain"
            defaultValue={domain}
            placeholder={searchCopy.domainPlaceholder}
            className={`${fieldInputClass} w-full`}
          />
        </label>
        <label className="flex flex-col gap-2" htmlFor="benchmark-phone">
          <span className={fieldLabelClass}>{searchCopy.phoneLabel}</span>
          <input
            id="benchmark-phone"
            type="tel"
            name="phone"
            placeholder={searchCopy.phonePlaceholder}
            className={`${fieldInputClass} w-full`}
          />
        </label>
      </div>
      <button type="submit" className={longButtonClass}>
        {searchCopy.missingCta}
      </button>
    </form>
  )
}

export default function BenchmarkSearchSection() {
  const [query, setQuery] = useState("")
  const [submitted, setSubmitted] = useState("")
  const [selectedId, setSelectedId] = useState(null)

  const matches = submitted ? findAuditMatches(submitted, auditCompanies) : []
  const selected = matches.find((company) => company.id === selectedId) ?? null
  const domainPrefill = submitted.includes(".") && !submitted.includes(" ") ? submitted.trim() : ""

  function onSubmit(event) {
    event.preventDefault()
    setSubmitted(query.trim())
    setSelectedId(null)
  }

  return (
    <section className="flex w-full translate-x-[calc(50vw-50%-1.5rem)] flex-col gap-8">
      <h2 className={headingClass}>{searchCopy.title}</h2>
      <form className="flex w-full flex-col items-start gap-3 sm:flex-row sm:items-center" onSubmit={onSubmit}>
        <input
          type="search"
          value={query}
          placeholder={searchCopy.placeholder}
          aria-label="Company name, domain, or city"
          onChange={(event) => setQuery(event.target.value)}
          className={`${fieldInputClass} w-full min-w-0 flex-1`}
        />
        <button type="submit" className={`${formButtonClass} shrink-0 gap-1.5`}>
          <Search className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
          {searchCopy.button}
        </button>
      </form>
      {submitted && matches.length === 0 ? <MissingCard key={submitted} domain={domainPrefill} /> : null}
      {selected ? <AuditCard company={selected} /> : null}
      {matches.length > 0 && !selected ? (
        <ul className={`m-0 flex w-full list-none flex-col overflow-hidden rounded-xl bg-white p-0 ${cardShadow}`}>
          {matches.map((company) => (
            <li
              key={company.id}
              className="border-b border-solid border-black/[0.08] last:border-b-0"
            >
              <button
                type="button"
                onClick={() => setSelectedId(company.id)}
                className={`flex w-full cursor-pointer items-center justify-between gap-4 bg-white px-6 py-4 text-left transition-colors duration-100 hover:bg-[#FAFAFA] ${focusRing}`}
              >
                <span className="text-[16px] font-medium leading-6 text-[#171717]">
                  {company.name}
                </span>
                <span className="shrink-0 text-[14px] font-normal leading-5 text-[#4D4D4D]">
                  {company.location}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
